// Pure reads the queue table needs. Beside `row-fields`, which the review panel shares too.

import { OTHER_EXAM_KEY, STATUS } from './constants';
import { rec, sub, displayName } from './row-fields';

// `include_count` makes the service answer with an envelope instead of a bare array, and the
// pager needs the total. Both shapes, and the null first render, land here.
export const asPage = data => {
  if (Array.isArray(data)) return { items: data, count: data.length };
  if (data && Array.isArray(data.items)) {
    return {
      items: data.items,
      count: typeof data.count === 'number' ? data.count : data.items.length,
    };
  }
  return { items: [], count: 0 };
};

export const recordName = r => displayName(r) || 'this record';

// An assembled record carries the readable name; only an unassembled row asks the library.
// `OTHER` is the tutor's escape hatch, where what they typed is the only name there is.
export const examLabel = (r, examNames) => {
  const s = sub(r);
  if (rec(r).test) return rec(r).test;
  if (s.examKey === OTHER_EXAM_KEY && s.otherExamName) return s.otherExamName;
  return examNames[s.examKey] || s.examKey || '—';
};

// An `OTHER` submission puts the tutor's one phrase in both fields, so the score would
// reprint the exam line above it.
export const scoreLine = (r, examNames) => {
  const value = rec(r).score || sub(r).score;
  if (!value) return null;
  return String(value).trim() === String(examLabel(r, examNames)).trim() ? null : value;
};

export const initials = name => {
  const words = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '—';
  return words
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
};

// Only a parked row earns the full-width strip: "not assembled yet" is true of most of the
// queue, and as a strip it doubled every row's height.
export const parkedReason = r => (r.status === STATUS.PARKED ? r.parked_reason : null);

export const awaitingAssembly = r => !r.record && r.status !== STATUS.REMOVED;

// Publishing an unassembled draft always 409s, so Publish is not offered until the agent
// has built the record. Shared because the queue row and the review panel both offer the
// button, and if they disagreed one of them would show a button the service refuses.
export const canPublish = r => r.status === STATUS.DRAFT && !!r.record;

// --- search -----------------------------------------------------------------

// The words a moderator typed, lowercased. Whitespace-split rather than taken whole,
// because the three things they can search are in three different fields: "priya imo"
// means a student *and* an exam, and as one substring it matches nothing.
export const searchTerms = query =>
  String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

// The rendered strings, not the raw fields: a moderator searches for what they can see, and
// `displayName`/`examLabel` already encode which name wins.
const searchHaystack = (r, examNames) =>
  [displayName(r), examLabel(r, examNames)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// A UUID is 32 hex digits, so one hex character sits inside ~7 of every 8 of them: as a
// substring the "a" of "Aarav" matched almost the whole table. Prefix, and only from the
// first dash-separated group, so nothing typed as a name reaches the id.
const ID_SEARCH_MINIMUM = 8;

const matchesId = (r, term) =>
  term.length >= ID_SEARCH_MINIMUM &&
  String(r.id || '')
    .toLowerCase()
    .startsWith(term);

// Every word has to appear somewhere, in any order and across any of the fields.
export const matchesSearch = (r, terms, examNames) => {
  const haystack = searchHaystack(r, examNames);
  return terms.every(term => haystack.includes(term) || matchesId(r, term));
};
